import React, { useState, useRef } from "react";
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../../config/emailjs-config';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const myForm = useRef(null);

  const changeHandler = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters long';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = (event) => {
    event.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('');

    // Send email using EmailJS with template
    const templateParams = {
      to_email: EMAILJS_CONFIG.TO_EMAIL,
      from_name: formData.name,
      from_email: formData.email,
      user_email: formData.email, // Alternative variable name
      email: formData.email, // Another common variable name
      subject: formData.subject || 'Contact from MobiCloud Website',
      message: formData.message,
      reply_to: formData.email,
      user_name: formData.name, // Alternative variable name
      name: formData.name // Another common variable name
    };

    console.log('Sending email with params:', templateParams);

    emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      templateParams,
      EMAILJS_CONFIG.PUBLIC_KEY
    )
    .then((response) => {
      console.log('SUCCESS!', response.status, response.text);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      myForm.current.reset();
    })
    .catch((err) => {
      console.log('FAILED...', err);
      setSubmitStatus('error');
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  };

  return (
    <div className="contact-box text-center">
      <form
        ref={myForm}
        onSubmit={submitHandler}
        className="contact-form"
        noValidate="novalidate"
      >
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                name="name"
                placeholder="Name"
                required="required"
                onChange={changeHandler}
                value={formData.name}
              />
              {errors.name && (
                <div className="invalid-feedback">
                  {errors.name}
                </div>
              )}
            </div>
            <div className="form-group">
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                name="email"
                placeholder="Email"
                required="required"
                onChange={changeHandler}
                value={formData.email}
              />
              {errors.email && (
                <div className="invalid-feedback">
                  {errors.email}
                </div>
              )}
            </div>
            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.subject ? 'is-invalid' : ''}`}
                name="subject"
                placeholder="Subject"
                required="required"
                onChange={changeHandler}
                value={formData.subject}
              />
              {errors.subject && (
                <div className="invalid-feedback">
                  {errors.subject}
                </div>
              )}
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <textarea
                className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                name="message"
                placeholder="Message"
                required="required"
                onChange={changeHandler}
                value={formData.message}
                rows="5"
              />
              {errors.message && (
                <div className="invalid-feedback">
                  {errors.message}
                </div>
              )}
            </div>
          </div>
          <div className="col-12">
            <button
              type="submit"
              className="btn btn-lg btn-block mt-3"
              disabled={isSubmitting}
            >
              <span className="text-white pr-3">
                <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
              </span>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="alert alert-success mt-3" role="alert">
                <i className="fas fa-check-circle me-2"></i>
                Thank you! Your message has been sent successfully to admin@mobicloud.ai
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="alert alert-danger mt-3" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>
                Sorry, there was an error sending your message. Please try again or contact us directly at admin@mobicloud.ai
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;